import { useState, useRef } from "react";
import { useParams, Link } from "wouter";
import { getDemoProfile, DEMO_PROFILES } from "@/data/demoProfiles";
import { Navbar } from "@/components/layout/Navbar";
import ProfilePreview from "@/components/dashboard/ProfilePreview";
import {
  User, QrCode, Eye, BarChart3, ArrowLeft, ArrowRight,
  Info, TrendingUp, Activity, Calendar, Clock, Smartphone,
  ExternalLink, Download,
} from "lucide-react";
import QRCodeComponent from "react-qr-code";

type Tab = "profile" | "qr" | "preview" | "analytics";

const TABS: { id: Tab; label: string; icon: React.ElementType }[] = [
  { id: "profile",   label: "My Profile", icon: User },
  { id: "qr",       label: "QR Code",    icon: QrCode },
  { id: "preview",  label: "Preview",    icon: Eye },
  { id: "analytics",label: "Analytics",  icon: BarChart3 },
];

const PLATFORM_BG: Record<string, string> = {
  instagram: "bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600",
  whatsapp:  "bg-[#25D366]",
  facebook:  "bg-[#1877F2]",
  twitter:   "bg-black",
  linkedin:  "bg-[#0A66C2]",
  youtube:   "bg-[#FF0000]",
  tiktok:    "bg-black",
  email:     "bg-blue-500",
  phone:     "bg-green-600",
  website:   "bg-slate-700",
  location:  "bg-red-500",
  payment:   "bg-indigo-500",
  telegram:  "bg-[#2CA5E0]",
};

function InfoBadge({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800 mb-6">
      <Info className="w-4 h-4 shrink-0 mt-0.5 text-amber-500" />
      <span>{text}</span>
    </div>
  );
}

export default function DemoProfileViewer() {
  const { slug } = useParams<{ slug: string }>();
  const demo = getDemoProfile(slug || "");
  const [activeTab, setActiveTab] = useState<Tab>("profile");
  const qrRef = useRef<HTMLDivElement>(null);

  if (!demo) {
    return (
      <div className="min-h-screen bg-muted/20">
        <Navbar />
        <div className="max-w-xl mx-auto px-4 py-32 text-center">
          <h1 className="text-2xl font-bold mb-3">Demo Not Found</h1>
          <p className="text-muted-foreground mb-8">We couldn't find that demo profile.</p>
          <Link href="/demos" className="px-6 py-3 bg-primary text-white font-bold rounded-xl inline-block">
            ← Back to Demos
          </Link>
        </div>
      </div>
    );
  }

  const profileUrl = `https://commonqr.com/demos/${demo.slug}`;

  const downloadQR = () => {
    if (!qrRef.current) return;
    const svg = qrRef.current.querySelector("svg");
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.onload = () => {
      canvas.width = img.width + 60;
      canvas.height = img.height + 60;
      if (ctx) {
        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 30, 30);
        const pngFile = canvas.toDataURL("image/png");
        const a = document.createElement("a");
        a.download = `CommonQR_Demo_${demo.slug}.png`;
        a.href = pngFile;
        a.click();
      }
    };
    img.src = "data:image/svg+xml;base64," + btoa(svgData);
  };

  const currentIdx = DEMO_PROFILES.findIndex((p) => p.slug === demo.slug);
  const prevDemo = currentIdx > 0 ? DEMO_PROFILES[currentIdx - 1] : null;
  const nextDemo = currentIdx < DEMO_PROFILES.length - 1 ? DEMO_PROFILES[currentIdx + 1] : null;

  const mockScans = Array.from({ length: 8 }, (_, i) => ({
    id: i,
    time: new Date(Date.now() - i * 3600000 * (1 + Math.random() * 4)),
    location: ["Mumbai, India", "New York, USA", "London, UK", "Singapore", "Dubai, UAE", "Toronto, CA", "Sydney, AU", "Berlin, DE"][i],
  }));

  return (
    <div className="min-h-screen bg-muted/20 pb-20">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">

        {/* Breadcrumb */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3 text-sm">
            <Link href="/demos" className="flex items-center gap-1 text-muted-foreground hover:text-primary transition-colors font-medium">
              <ArrowLeft className="w-4 h-4" /> Demo Profiles
            </Link>
            <span className="text-border">/</span>
            <span className="font-semibold text-foreground">{demo.name}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs bg-amber-100 text-amber-700 font-bold px-3 py-1 rounded-full">DEMO</span>
            <Link href="/signup" className="px-4 py-2 bg-primary text-primary-foreground font-bold rounded-xl text-sm shadow-sm hover:shadow-md transition-all inline-block">
              Create Your Own →
            </Link>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-8">

          {/* Sidebar */}
          <aside className="w-full md:w-56 shrink-0">
            <div className="bg-card rounded-2xl border border-border/50 p-2 shadow-sm sticky top-24">
              <nav className="flex flex-row md:flex-col gap-1 overflow-x-auto pb-2 md:pb-0">
                {TABS.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all whitespace-nowrap ${
                        activeTab === tab.id
                          ? "bg-primary text-primary-foreground shadow-md shadow-primary/20"
                          : "text-muted-foreground hover:bg-secondary"
                      }`}
                    >
                      <Icon className="w-5 h-5" /> {tab.label}
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Demo profile mini card */}
            <div className="mt-4 bg-card rounded-2xl border border-border/50 p-4 shadow-sm hidden md:block">
              <div className={`h-14 rounded-xl bg-gradient-to-r ${demo.coverFrom} ${demo.coverTo} relative mb-8`}>
                <div className={`absolute -bottom-5 left-3 w-10 h-10 rounded-full border-2 border-white ${demo.avatarColor} flex items-center justify-center text-white font-bold`}>
                  {demo.avatarInitial}
                </div>
              </div>
              <p className="font-bold text-sm">{demo.name}</p>
              <p className="text-xs text-muted-foreground">@{demo.username}</p>
              <p className="text-xs text-muted-foreground mt-1">{demo.location}</p>
            </div>

            {/* Prev/Next */}
            <div className="mt-4 hidden md:flex flex-col gap-2">
              {prevDemo && (
                <Link href={`/demos/${prevDemo.slug}`} className="flex items-center gap-2 text-xs text-muted-foreground hover:text-primary px-3 py-2 rounded-xl border border-border hover:border-primary transition-all">
                  <ArrowLeft className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate">{prevDemo.name}</span>
                </Link>
              )}
              {nextDemo && (
                <Link href={`/demos/${nextDemo.slug}`} className="flex items-center gap-2 text-xs text-muted-foreground hover:text-primary px-3 py-2 rounded-xl border border-border hover:border-primary transition-all justify-between">
                  <span className="truncate">{nextDemo.name}</span>
                  <ArrowRight className="w-3.5 h-3.5 shrink-0" />
                </Link>
              )}
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 bg-card rounded-3xl border border-border/50 shadow-xl shadow-black/5 p-6 md:p-8 min-h-[600px]">

            {/* ── My Profile Tab ── */}
            {activeTab === "profile" && (
              <div className="max-w-3xl">
                <div className="mb-8">
                  <h2 className="text-2xl font-display font-bold">My Profile</h2>
                  <p className="text-muted-foreground text-sm mt-1">Profile details for this demo use case.</p>
                </div>

                <InfoBadge text="This is a demo profile showing a pre-filled example. Create your account to build and customise your own." />

                {/* Profile Info */}
                <section className="bg-background rounded-2xl border border-border/60 p-6 space-y-5 mb-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                      <User className="w-5 h-5" />
                    </div>
                    <h3 className="font-bold text-lg">Profile Info</h3>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {[
                      { label: "Display Name", value: demo.profileFormData.name },
                      { label: "Location", value: demo.profileFormData.location },
                    ].map((f) => (
                      <div key={f.label}>
                        <label className="block text-xs font-medium text-muted-foreground mb-1">{f.label}</label>
                        <div className="rounded-xl bg-muted/50 border-2 border-border px-4 py-2.5 text-sm">{f.value}</div>
                      </div>
                    ))}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Bio / Tagline</label>
                    <div className="rounded-xl bg-muted/50 border-2 border-border px-4 py-2.5 text-sm">{demo.profileFormData.bio}</div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {[
                      { label: "Public Email", value: demo.profileFormData.email },
                      { label: "Phone", value: demo.profileFormData.phone },
                    ].map((f) => (
                      <div key={f.label}>
                        <label className="block text-xs font-medium text-muted-foreground mb-1">{f.label}</label>
                        <div className="rounded-xl bg-muted/50 border-2 border-border px-4 py-2.5 text-sm text-muted-foreground">{f.value}</div>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Links */}
                <section className="bg-background rounded-2xl border border-border/60 p-6">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                      <ExternalLink className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">Social &amp; Platform Links</h3>
                      <p className="text-xs text-muted-foreground">Links in recommended order for this profile type</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {demo.links.map((link) => {
                      const bg = PLATFORM_BG[link.platform] || "bg-slate-600";
                      return (
                        <div key={link.sortOrder} className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl ${bg} text-white flex items-center justify-center shrink-0`}>
                            <span className="text-xs font-bold uppercase">{link.platform.slice(0, 2)}</span>
                          </div>
                          <span className="w-28 shrink-0 text-sm font-medium text-muted-foreground hidden sm:block">{link.label}</span>
                          <div className="flex-1 rounded-xl bg-muted/50 border-2 border-border px-4 py-2.5 text-sm text-muted-foreground truncate">
                            {link.url}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              </div>
            )}

            {/* ── QR Code Tab ── */}
            {activeTab === "qr" && (
              <div className="max-w-2xl mx-auto">
                <div className="mb-8">
                  <h2 className="text-2xl font-display font-bold">QR Code</h2>
                  <p className="text-muted-foreground text-sm mt-1">Scan this QR code to open this demo profile.</p>
                </div>

                <InfoBadge text="This demo QR code points to this demo page. A real profile's QR always points to your live public profile." />

                <div className="flex flex-col items-center">
                  <div className="bg-card p-8 rounded-[2rem] shadow-2xl shadow-primary/10 border border-border/50 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-tr from-primary/5 to-transparent pointer-events-none" />
                    <div ref={qrRef} className="relative z-10 bg-white p-3 rounded-xl">
                      <QRCodeComponent value={profileUrl} size={220} level="H" fgColor="#000000" bgColor="#FFFFFF" />
                    </div>
                  </div>
                  <div className="mt-4">
                    <span className="text-sm font-mono text-muted-foreground">
                      commonqr.com/demos/<span className="text-primary font-semibold">{demo.slug}</span>
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
                    <button
                      onClick={downloadQR}
                      className="flex items-center justify-center gap-2 px-8 py-4 bg-primary text-white font-bold rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
                    >
                      <Download className="w-5 h-5" /> Download QR (PNG)
                    </button>
                    <Link
                      href="/signup"
                      className="flex items-center justify-center gap-2 px-8 py-4 border-2 border-primary text-primary font-bold rounded-xl hover:bg-primary/5 transition-colors"
                    >
                      <ExternalLink className="w-5 h-5" /> Create Your Own
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {/* ── Preview Tab ── */}
            {activeTab === "preview" && (
              <ProfilePreview
                profile={{
                  name: demo.name,
                  bio: demo.bio,
                  location: demo.location,
                  username: demo.username,
                  scanCount: demo.totalScans,
                  coverFrom: demo.coverFrom,
                  coverTo: demo.coverTo,
                }}
                links={demo.links.map((l) => ({ id: l.sortOrder, platform: l.platform, label: l.label, url: l.url, sortOrder: l.sortOrder }))}
                allowReorder
                isDemo
              />
            )}

            {/* ── Analytics Tab ── */}
            {activeTab === "analytics" && (
              <div>
                <div className="flex items-start justify-between mb-8">
                  <div>
                    <h2 className="text-2xl font-display font-bold">Analytics</h2>
                    <p className="text-muted-foreground text-sm mt-1">Mock analytics for this demo profile.</p>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/50 px-3 py-1.5 rounded-full border border-border">
                    <Info className="w-3.5 h-3.5" /> Demo data
                  </div>
                </div>

                <InfoBadge text="These are sample analytics numbers showing what you'd see in your real dashboard. Actual scan tracking starts the moment you create your profile." />

                {/* KPI Grid */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
                  {[
                    { label: "Total Scans", value: demo.totalScans.toLocaleString(), sub: "All time", icon: BarChart3, hero: true },
                    { label: "Today", value: demo.todayScans, sub: "Scans today", icon: Activity, color: "text-green-600", bg: "bg-green-50" },
                    { label: "This Week", value: demo.weekScans, sub: "Last 7 days", icon: Calendar, color: "text-blue-600", bg: "bg-blue-50" },
                    { label: "This Month", value: demo.monthScans, sub: "Last 30 days", icon: TrendingUp, color: "text-orange-600", bg: "bg-orange-50" },
                    { label: "Daily Average", value: (demo.monthScans / 30).toFixed(1), sub: "Scans/day (30d)", icon: Clock, color: "text-pink-600", bg: "bg-pink-50" },
                    { label: "Last Scanned", value: "Just now", sub: "A few seconds ago", icon: Smartphone, color: "text-muted-foreground", bg: "bg-muted" },
                  ].map((kpi, i) => {
                    const Icon = kpi.icon;
                    if (kpi.hero) {
                      return (
                        <div key={i} className="rounded-2xl p-6 bg-gradient-to-br from-violet-500 to-purple-600 text-white shadow-xl shadow-purple-500/20 sm:col-span-2 lg:col-span-1">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-medium text-white/80">{kpi.label}</span>
                            <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                              <Icon className="w-4 h-4 text-white" />
                            </div>
                          </div>
                          <p className="text-4xl font-bold mb-1">{kpi.value}</p>
                          <p className="text-sm text-white/70">{kpi.sub}</p>
                        </div>
                      );
                    }
                    return (
                      <div key={i} className="rounded-2xl p-6 bg-card border border-border shadow-sm">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-medium text-muted-foreground">{kpi.label}</span>
                          <div className={`w-8 h-8 rounded-lg ${kpi.bg} flex items-center justify-center`}>
                            <Icon className={`w-4 h-4 ${kpi.color}`} />
                          </div>
                        </div>
                        <p className={`text-3xl font-bold ${kpi.color}`}>{kpi.value}</p>
                        <p className="text-xs text-muted-foreground mt-1">{kpi.sub}</p>
                      </div>
                    );
                  })}
                </div>

                {/* Mock recent activity */}
                <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-3">Recent Activity</h3>
                <div className="space-y-2">
                  {mockScans.map((scan) => (
                    <div key={scan.id} className="flex items-center gap-4 py-3 px-4 bg-muted/30 rounded-xl border border-border text-sm">
                      <div className="w-2 h-2 rounded-full bg-green-400 shrink-0 animate-pulse" />
                      <span className="font-medium w-44 shrink-0">
                        {scan.time.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                      </span>
                      <span className="text-muted-foreground truncate flex-1">{scan.location}</span>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <div className="mt-10 p-6 bg-gradient-to-br from-primary/5 to-purple-50 border border-primary/20 rounded-2xl text-center">
                  <p className="font-bold text-base mb-2">Want real analytics for your profile?</p>
                  <p className="text-sm text-muted-foreground mb-4">Every scan of your QR is tracked automatically. See where, when, and how often.</p>
                  <Link
                    href="/signup"
                    className="inline-flex items-center gap-2 bg-primary text-white font-bold px-6 py-3 rounded-xl shadow-sm hover:shadow-md transition-all"
                  >
                    Start Free — No Credit Card <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            )}

          </main>
        </div>
      </div>
    </div>
  );
}
